import { type GroupNatureIndex, type NatureIndex } from '@repair/catalog/types/index/nature';

import { type Brand } from './dictionaries.type';
import { type MasterDataArticle, type Article } from './masterdata/masterdata.type';
import { type OrderRaw } from '../pages/SparePartOrders/types';

export enum LevelType {
  processes = 'processes',
  categories = 'categories',
  natures = 'natures',
  products = 'products',
  product = 'product',
}

export type Source = 'DECATHLON' | 'MARKETPLACE';

export interface Thumbnail {
  nextLevel: LevelType;
  entities: ILinkEntity[];
}

// Overrides id to be string and let the initial ILinkEntity definition for services/spare parts
export interface ILinkProduct extends Omit<ILinkEntity, 'id'> {
  id?: string;
  brand?: Brand | null;
  purchaseDate?: Date;
  articles: MasterDataArticle[];
  traceabilityNumber?: string;
  stringTension?: string;
  location?: string | null;
  isFromPurchase?: boolean;
  groupNatureLevelOne?: GroupNatureIndex;
  groupNatureLevelTwo?: GroupNatureIndex;
  groupNatureLevelThree?: GroupNatureIndex;
  nature?: NatureIndex;
  sportIds?: number[];
  structuredCharacteristicIds?: number[];
  process?: Process | null;
  category?: Category | null;
  provider?: Source;
}

export interface ILinkProductRaw extends ILinkEntity {
  brand?: Brand;
  purchaseDate?: string;
  articles: MasterDataArticle[];
  traceabilityNumber?: string;
  stringTension?: string;
  location?: string | null;
  isFromPurchase?: boolean;
}

export interface ILinkProcess {
  id: number;
  name: string;
}

export interface ILinkCategory extends ILinkProcess {
  imageUrl?: string;
}

export interface ILinkEntity {
  id: number;
  label: string;
  imageUrl?: string;
  isCustom: boolean | null;
  ranking?: number;
}

export interface ILinkProductResponse {
  product_ids: number[];
}

export interface ILinkEntityResponse {
  id: number;
  label: string;
  image_url?: string;
  model_code: number;
  ranking: number;
}

export interface CompatibilityModel {
  spare_part_id: number;
  compatibility: boolean | undefined;
  hypothesis: boolean | undefined;
}

export interface CompatibilitySparePartNature {
  spare_part_models: CompatibilityModel[];
  spare_part_nature_id: number;
}

export interface ILinkSparePartNature {
  spare_part_models: number[];
  spare_part_nature_id: number;
}

export interface ILinkServicesNature {
  service_ids: number[];
  spare_part_nature_id: number;
}

interface AssociatedNatureEntity {
  id: number;
  natureIds: number[];
}

export interface ILinkSparePartsAndServicesNature {
  services: AssociatedNatureEntity[];
  spareParts: AssociatedNatureEntity[];
}

export interface ILinkNature {
  natureId: number;
  natureTitle: string;
}

export interface Entity {
  id?: string;
  name: string;
  modelCode: number;
  quantity: number;
  imageUrl?: string;
  articles: Article[];
  related?: boolean;
  cycle?: string;
  unitQuantity?: number;
  process?: ILinkProcess;
  filtered?: boolean;
  compatibility?: boolean;
  hypothesis?: boolean;
  orders?: OrderRaw[];
  nature?: NatureIndex;
  provider?: Source;
  catchLine?: string;
}

export interface SparePartEntity extends Entity {
  orderNumbers?: number[];
}

export interface InterventionNature extends ILinkNature {
  spareParts: SparePartEntity[];
  services: Entity[];
  selected?: boolean;
}

export interface NatureWithCategory extends InterventionNature {
  categoryId: number;
}

export interface ILinkCategoryRaw {
  id: number;
  label: string;
  image_url: string;
  pixl_id: string;
  ranking?: number;
}

export interface ProductInformation {
  process: ILinkProcess;
  category: ILinkCategory;
}

export interface ILinkNatureCategory {
  id: number;
  label: string;
  imageUrl: string;
  natureIds: number[];
}

export interface NatureCategory {
  id: number;
  label: string;
  imageUrl?: string;
  natures: InterventionNature[];
  selected?: boolean;
}

export interface IlinkGroupProductNaturesContent {
  id: number;
  label: string;
  imageURL: string;
  groupProductNatureAssociations: GroupProductNatureAssociation[];
}

interface GroupProductNatureAssociation {
  uuid: string;
  groupProductNatureID: number;
  productNatureID: number;
}

export interface Process {
  id: number | null;
  label?: string;
  pixl_id?: string;
  ranking?: number | null;
}

export interface Category {
  id: number | null;
  label?: string;
  pixl_id?: string;
  ranking?: number | null;
}
